import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Security, ToggleOn, ToggleOff, Search, CheckCircle, Cancel, PhoneAndroid, Email } from '@mui/icons-material';
import UserApiService from 'api/UserApiService';
import mfaApiService from 'api/MfaApiService';
import { useAppState } from 'store/Provider';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import User from 'types/entities/User';
import useApi from 'hooks/useApi';
import './AdminMfaManagement.scss';

type MfaType = 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';

const MFA_METHOD_LABEL: Record<MfaType, { label: string; icon: React.ReactNode }> = {
  SOFTWARE_TOKEN_MFA: { label: 'Authenticator', icon: <PhoneAndroid fontSize="small" /> },
  EMAIL_OTP: { label: 'Email OTP', icon: <Email fontSize="small" /> },
};

const getBatchMfaSetupCompleted = (currentValue: boolean, enabled: boolean, mfaType: MfaType) => {
  if (!enabled) return currentValue;
  return mfaType === 'EMAIL_OTP';
};

const AdminMfaManagement: React.FC = () => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const loadings = useSelector((state) => state.loadings);
  const { makeCall } = useApi();
  const selectedCustomerId = appState.customer?.id;

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'enable' | 'disable'>('enable');
  const [selectedMfaType, setSelectedMfaType] = useState<MfaType>('SOFTWARE_TOKEN_MFA');
  const [processing, setProcessing] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filter states
  const [clientTypeFilter, setClientTypeFilter] = useState<'all' | 'internal' | 'external'>('all');

  // Batch selection states
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [showBatchConfirmDialog, setShowBatchConfirmDialog] = useState(false);
  const [batchActionType, setBatchActionType] = useState<'enable' | 'disable'>('enable');
  const [batchMfaType, setBatchMfaType] = useState<MfaType>('SOFTWARE_TOKEN_MFA');

  const loadUsers = useCallback(
    async (currentPage: number, pageSize: number) => {
      try {
        dispatch(addLoadingAction('loadUsers'));

        const queryParams: any = {
          size: pageSize,
          page: currentPage ?? 0,
          customer_id: selectedCustomerId,
          is_archived: 0,
        };

        if (searchTerm) {
          queryParams['last_name,first_name,mail'] = `:${searchTerm}:`;
        }

        if (clientTypeFilter !== 'all') {
          queryParams.is_client = clientTypeFilter === 'external' ? 1 : 0;
        }

        const response = await makeCall(UserApiService.get(queryParams), 'Unable to retrieve users');

        if (response && response.datas) {
          setUsers(response.datas);
          setTotalItems(response.totalItems || 0);
        } else {
          setUsers([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
        setTotalItems(0);
      } finally {
        dispatch(removeLoadingAction('loadUsers'));
      }
    },
    [clientTypeFilter, dispatch, makeCall, searchTerm, selectedCustomerId]
  );

  const filtersSignature = `${searchTerm}|${clientTypeFilter}|${selectedCustomerId ?? ''}`;
  const previousFiltersSignature = useRef(filtersSignature);

  useEffect(() => {
    if (!selectedCustomerId) {
      setUsers([]);
      setTotalItems(0);
      return;
    }

    // When the filters change, go back to the first page and let that reset trigger the reload.
    if (previousFiltersSignature.current !== filtersSignature) {
      previousFiltersSignature.current = filtersSignature;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    loadUsers(page, rowsPerPage);
  }, [filtersSignature, loadUsers, page, rowsPerPage, selectedCustomerId]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [selectedCustomerId]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleMfa = (user: User, action: 'enable' | 'disable') => {
    setSelectedUser(user);
    setActionType(action);
    setSelectedMfaType('SOFTWARE_TOKEN_MFA');
    setShowConfirmDialog(true);
  };

  const handleConfirmToggle = async () => {
    if (!selectedUser) return;

    try {
      setProcessing(true);
      const enabled = actionType === 'enable';

      await mfaApiService.adminSetUserMfa(selectedUser.id, enabled, enabled ? selectedMfaType : undefined);

      if (enabled) {
        sessionStorage.removeItem('mfa_check_done');
        sessionStorage.removeItem('mfa_check_result');
      }

      setUsers(
        users.map((u) =>
          u.id === selectedUser.id
            ? {
              ...u,
              mfa_enabled: enabled,
              mfa_preference: enabled ? selectedMfaType : null,
              mfa_setup_completed: enabled && selectedMfaType === 'EMAIL_OTP' ? true : u.mfa_setup_completed,
            }
            : u
        )
      );

      const userName = `${selectedUser.first_name} ${selectedUser.last_name}`;
      let successMessage = `MFA has been disabled for ${userName}`;
      if (enabled && selectedMfaType === 'EMAIL_OTP') {
        successMessage = `Email OTP MFA enabled for ${userName}. A code will be sent to their email on each login.`;
      } else if (enabled) {
        successMessage = `Authenticator App MFA enabled for ${userName}. User will be prompted to set up on next login.`;
      }

      dispatch(
        setSnackbarAction({
          open: true,
          message: successMessage,
          severity: 'success',
        })
      );

      setShowConfirmDialog(false);
      setSelectedUser(null);
    } catch (error: any) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: error.response?.data?.message || `Failed to ${actionType} MFA`,
          severity: 'error',
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
    setSelectedUser(null);
  };

  // Batch operations
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const eligibleUserIds = filteredUsers.filter((user) => user.cognito_username).map((user) => user.id);
      setSelectedUserIds(eligibleUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleBatchAction = (action: 'enable' | 'disable') => {
    if (selectedUserIds.length === 0) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: 'Please select at least one user',
          severity: 'warning',
        })
      );
      return;
    }
    setBatchActionType(action);
    setBatchMfaType('SOFTWARE_TOKEN_MFA');
    setShowBatchConfirmDialog(true);
  };

  const handleConfirmBatchAction = async () => {
    try {
      setProcessing(true);
      const enabled = batchActionType === 'enable';

      const promises = selectedUserIds.map((userId) =>
        mfaApiService.adminSetUserMfa(userId, enabled, enabled ? batchMfaType : undefined)
      );

      await Promise.all(promises);

      if (enabled) {
        sessionStorage.removeItem('mfa_check_done');
        sessionStorage.removeItem('mfa_check_result');
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          selectedUserIds.includes(user.id)
            ? {
              ...user,
              mfa_enabled: enabled,
              mfa_preference: enabled ? batchMfaType : null,
              mfa_setup_completed: getBatchMfaSetupCompleted(user.mfa_setup_completed, enabled, batchMfaType),
            }
            : user
        )
      );

      dispatch(
        setSnackbarAction({
          open: true,
          message: `MFA has been ${enabled ? 'enabled' : 'disabled'} for ${selectedUserIds.length} user(s)`,
          severity: 'success',
        })
      );

      setShowBatchConfirmDialog(false);
      setSelectedUserIds([]);
    } catch (error: any) {
      dispatch(
        setSnackbarAction({
          open: true,
          message: error.response?.data?.message || `Failed to ${batchActionType} MFA for selected users`,
          severity: 'error',
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelBatchAction = () => {
    setShowBatchConfirmDialog(false);
  };

  const renderMfaMethodChip = (preference: User['mfa_preference']) => {
    if (!preference)
      return (
        <Typography variant="caption" color="textSecondary">
          —
        </Typography>
      );
    const info = MFA_METHOD_LABEL[preference as MfaType];
    if (!info) return <Typography variant="caption">{preference}</Typography>;
    return (
      <Chip icon={info.icon as React.ReactElement} label={info.label} size="small" variant="outlined" color="default" />
    );
  };

  const filteredUsers = useMemo(
    () => users.filter((u) => !selectedCustomerId || u.customer_id === selectedCustomerId),
    [users, selectedCustomerId]
  );

  useEffect(() => {
    setSelectedUserIds((prev) => {
      const validIds = new Set(filteredUsers.filter((u) => u.cognito_username).map((u) => u.id));
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredUsers]);

  const eligibleUsersCount = filteredUsers.filter((u) => u.cognito_username).length;
  const isAllSelected = eligibleUsersCount > 0 && selectedUserIds.length === eligibleUsersCount;
  const isSomeSelected = selectedUserIds.length > 0 && selectedUserIds.length < eligibleUsersCount;

  return (
    <Box className="admin-mfa-container">
      <Paper className="admin-mfa-paper">
        <Box className="admin-mfa-header">
          <Box className="admin-mfa-title">
            <Security className="admin-mfa-icon" />
            <Typography variant="h5">Multi-Factor Authentication Management</Typography>
          </Box>
          <Box className="admin-mfa-controls">
            <FormControl size="small" className="admin-mfa-filter">
              <InputLabel>Client Type</InputLabel>
              <Select
                value={clientTypeFilter}
                label="Client Type"
                onChange={(e) => setClientTypeFilter(e.target.value as 'all' | 'internal' | 'external')}
              >
                <MenuItem value="all">All users</MenuItem>
                <MenuItem value="internal">Internal Only</MenuItem>
                <MenuItem value="external">External Only</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              className="admin-mfa-search"
            />
          </Box>
        </Box>

        {/* Batch Action Toolbar */}
        {selectedUserIds.length > 0 && (
          <Box className="admin-mfa-batch-toolbar">
            <Typography variant="subtitle1" className="admin-mfa-batch-selected">
              {selectedUserIds.length} user(s) selected
            </Typography>
            <Box className="admin-mfa-batch-actions">
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleBatchAction('enable')}
                startIcon={<ToggleOn />}
              >
                Enable MFA
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleBatchAction('disable')}
                startIcon={<ToggleOff />}
              >
                Disable MFA
              </Button>
              <Button variant="outlined" size="small" onClick={() => setSelectedUserIds([])}>
                Clear Selection
              </Button>
            </Box>
          </Box>
        )}

        {loadings.loadUsers ? (
          <Box className="admin-mfa-loading">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isSomeSelected}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      disabled={eligibleUsersCount === 0}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Client Type</TableCell>
                  <TableCell align="center">MFA Status</TableCell>
                  <TableCell align="center">Method</TableCell>
                  <TableCell align="center">Setup Completed</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} selected={selectedUserIds.includes(user.id)} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          disabled={!user.cognito_username}
                        />
                      </TableCell>
                      <TableCell>
                        {user.last_name} {user.first_name}
                      </TableCell>
                      <TableCell>{user.mail}</TableCell>
                      <TableCell align="center">
                        {!user.is_client ? (
                          <Chip label="Internal" color="info" size="small" />
                        ) : (
                          <Chip label="External" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {user.mfa_enabled ? (
                          <Chip icon={<CheckCircle />} label="Enabled" color="success" size="small" />
                        ) : (
                          <Chip icon={<Cancel />} label="Disabled" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">{renderMfaMethodChip(user.mfa_preference)}</TableCell>
                      <TableCell align="center">
                        {user.mfa_setup_completed ? (
                          <Chip label="Yes" color="primary" size="small" />
                        ) : (
                          <Chip label="No" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {user.mfa_enabled ? (
                          <Tooltip title="Disable MFA">
                            <IconButton color="error" size="small" onClick={() => handleToggleMfa(user, 'disable')}>
                              <ToggleOff />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Enable MFA">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleToggleMfa(user, 'enable')}
                              disabled={!user.cognito_username}
                            >
                              <ToggleOn />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Single-user Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelToggle}>
        <DialogTitle>{actionType === 'enable' ? 'Enable' : 'Disable'} MFA for User?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionType} MFA for{' '}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            ?
          </Typography>

          {actionType === 'enable' && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                MFA Method:
              </Typography>
              <RadioGroup value={selectedMfaType} onChange={(e) => setSelectedMfaType(e.target.value as MfaType)}>
                <FormControlLabel
                  value="SOFTWARE_TOKEN_MFA"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        <PhoneAndroid fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        Authenticator App (TOTP)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        User will be prompted to scan a QR code on next login.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="EMAIL_OTP"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        <Email fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        Email OTP
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Immediately active — a code is sent to the user&apos;s email each login. No setup required.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelToggle} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color={actionType === 'enable' ? 'primary' : 'error'}
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={24} /> : `${actionType === 'enable' ? 'Enable' : 'Disable'} MFA`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Confirmation Dialog */}
      <Dialog open={showBatchConfirmDialog} onClose={handleCancelBatchAction}>
        <DialogTitle>{batchActionType === 'enable' ? 'Enable' : 'Disable'} MFA for Multiple Users?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {batchActionType} MFA for{' '}
            <strong>{selectedUserIds.length} selected user(s)</strong>?
          </Typography>

          {batchActionType === 'enable' && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                MFA Method:
              </Typography>
              <RadioGroup value={batchMfaType} onChange={(e) => setBatchMfaType(e.target.value as MfaType)}>
                <FormControlLabel
                  value="SOFTWARE_TOKEN_MFA"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        <PhoneAndroid fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        Authenticator App (TOTP)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        All selected users will be prompted to scan a QR code on next login.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="EMAIL_OTP"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        <Email fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        Email OTP
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Immediately active for all selected users — no setup required.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>
          )}

          {batchActionType === 'disable' && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Note: This will disable MFA for all selected users immediately.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBatchAction} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatchAction}
            color={batchActionType === 'enable' ? 'primary' : 'error'}
            variant="contained"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={24} />
            ) : (
              `${batchActionType === 'enable' ? 'Enable' : 'Disable'} MFA for ${selectedUserIds.length} User(s)`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMfaManagement;
